import { useMemo, useState } from "react";
import { Search, Flame, Zap, Database, Waves, LayoutGrid, Info } from "lucide-react";
import Reveal from "./Reveal";
import UnitCard from "./UnitCard";

const FILTERS = [
  { key: "all", label: "ALL UNITS", icon: LayoutGrid },
  { key: "electric-storage", label: "ELECTRIC STORAGE", icon: Zap },
  { key: "electric-instantaneous", label: "ELECTRIC INSTANTANEOUS", icon: Zap },
  { key: "gas-storage", label: "GAS STORAGE", icon: Database },
  { key: "gas-continuous", label: "GAS CONTINUOUS", icon: Waves },
  { key: "gas-instantaneous", label: "GAS INSTANTANEOUS", icon: Flame },
];

export default function PriceList({ units, notes, onBookUnit }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [brand, setBrand] = useState("all");

  const brands = useMemo(() => [...new Set(units.map((u) => u.brand))].sort(), [units]);

  const visible = useMemo(() => {
    let list = units;
    if (filter !== "all") {
      const [fuel, system] = filter.split("-");
      list = list.filter((u) => u.fuel === fuel && u.system === system);
    }
    if (brand !== "all") list = list.filter((u) => u.brand === brand);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((u) => `${u.brand} ${u.model} ${u.id} ${u.size_class}`.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => a.price - b.price);
  }, [units, filter, brand, query]);

  const activeNotes = filter === "all" ? null : notes[filter];

  return (
    <section id="prices" className="bg-transparent relative z-20 pt-2 pb-20 sm:pb-28" data-testid="price-list-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal>
          <span className="chip-brutal bg-tpeco-blue border-tpeco-blue text-white">CHAPTER 02 — THE PRICE LIST</span>
          <h2 className="mt-4 font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="prices-heading">
            EVERY UNIT. <span className="bg-tpeco-pink text-white px-3 rounded-2xl inline-block">EVERY PRICE.</span><br />NO SALES GAMES.
          </h2>
          <p className="mt-4 max-w-2xl font-semibold text-neutral-700">
            Like-for-like swap-out pricing — unit, labour and materials included. Electric swaps include a qualified
            electrician (others do that step illegally — seriously). We always confirm the final price before work starts.
          </p>
        </Reveal>

        <Reveal i={1} className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search brand, size or product ID — try 'rheem 135' or 'H42'"
              className="w-full rounded-full border-[3px] border-tpeco-ink px-12 py-3 font-semibold shadow-[0_4px_0_0_#0A0A0A] focus:outline-none focus:ring-4 focus:ring-tpeco-blue/40"
              data-testid="price-search-input"
            />
          </div>
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="rounded-full border-[3px] border-tpeco-ink px-5 py-3 font-display uppercase text-lg shadow-[0_4px_0_0_#0A0A0A] bg-white focus:outline-none focus:ring-4 focus:ring-tpeco-blue/40"
            data-testid="brand-filter-select"
          >
            <option value="all">ALL BRANDS</option>
            {brands.map((b) => <option key={b} value={b}>{b.toUpperCase()}</option>)}
          </select>
        </Reveal>

        <Reveal i={2} className="mt-6 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`chip-brutal !px-4 !py-2 transition-colors ${filter === f.key ? "bg-tpeco-pink border-tpeco-pink text-white" : "bg-white hover:bg-tpeco-blue/10"}`}
            >
              <f.icon className="w-4 h-4" /> {f.label}
            </button>
          ))}
        </Reveal>

        {activeNotes && (
          <div className="mt-6 rounded-2xl border-2 border-tpeco-ink bg-tpeco-smoke p-4 flex gap-3" data-testid="category-notes">
            <Info className="w-5 h-5 shrink-0 mt-0.5" />
            <ul className="text-sm font-semibold space-y-1">
              {activeNotes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </div>
        )}

        <div className="mt-4 text-sm font-extrabold uppercase tracking-widest" data-testid="results-count">
          SHOWING {visible.length} OF {units.length} UNITS
        </div>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {visible.map((u, i) => <UnitCard key={u.id} unit={u} i={i % 4} onBook={onBookUnit} />)}
        </div>

        {visible.length === 0 && (
          <div className="mt-10 card-brutal p-10 text-center" data-testid="no-results">
            <p className="font-display text-3xl uppercase">NOTHING MATCHES THAT.</p>
            <p className="mt-2 font-semibold text-neutral-600">Try a different search — or text us a photo of your unit on 0475 776 555.</p>
          </div>
        )}

        <Reveal className="mt-12 card-brutal !bg-tpeco-blue text-white p-6 sm:p-8" data-testid="pricing-fine-print">
          <h3 className="font-display uppercase text-2xl flex items-center gap-2"><Flame className="w-5 h-5 text-tpeco-pink" /> THE FINE PRINT (IN BIG PRINT)</h3>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm font-semibold text-white/85">
            <p>· Prices are for a like-for-like swap — same style, size and power source in the same spot.</p>
            <p>· Valve kits: +$299 (storage) / +$188-$190 (instant) if required.</p>
            <p>· Unit inside a cabinet? Usually +$350.</p>
            <p>· Changing brand, size or style needs pipe alterations — quoted before work.</p>
            <p>· On LPG? Tell us when booking — LPG models are rare.</p>
            <p>· We always confirm the final price before commencing any work. Always.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
